import React from 'react'

import {
  Body2,
  Box,
  Breakpoint,
  Color,
  Display,
  DisplayPosition,
  FlexDirection,
  FontFamily,
  FontWeight,
  Image,
  JustifyContent,
  Text,
  withCitrusTheme,
} from '@gousto-internal/citrus-react'

export type RecipeCarouselCardRecipe = {
  id: string
  title: string
  imageUrl: string
  imageAlt: string
  cookTimeMins?: number
  kcal?: number
  dietaryClaims?: { slug: string }[]
  ariaLabel?: string
  onClick: () => void
}

type Props = {
  recipe: RecipeCarouselCardRecipe
}

const Card = withCitrusTheme('button', (theme) => ({
  appearance: 'none',
  font: 'inherit',
  color: 'inherit',
  textAlign: 'left',
  margin: 0,
  padding: 0,
  boxSizing: 'border-box',
  backgroundColor: theme.color(Color.White),
  borderRadius: '4px',
  border: `1px solid ${theme.color(Color.ColdGrey_100)}`,
  boxShadow: '0 3px 10px rgba(0, 0, 0, 0.10)',
  overflow: 'hidden',
  scrollSnapAlign: 'start',
  cursor: 'pointer',
  alignSelf: 'stretch',
  display: 'flex',
  flexDirection: 'column',
  flexGrow: 0,
  flexShrink: 0,
  width: '204px',
  [`@media (min-width: ${Breakpoint.Medium})`]: {
    width: '220px',
  },
  transition: 'box-shadow 150ms ease-in-out, transform 150ms ease-in-out',
  '&:hover': {
    transform: 'translateY(-3px)',
    boxShadow: '0 3px 10px rgba(0, 0, 0, 0.24)',
  },
  '&:focus-visible': {
    outline: `2px solid ${theme.color(Color.Secondary_400)}`,
    outlineOffset: '2px',
  },
}))

const DIETARY_ACRONYMS: Record<string, string> = {
  vegetarian: 'V',
  vegan: 'PB',
  'dairy-free': 'DF',
  'gluten-free': 'GF',
}

const IMAGE_HEIGHT = '200px'

const getDietaryBadges = (recipe: RecipeCarouselCardRecipe): string[] =>
  (recipe.dietaryClaims ?? [])
    .map((claim) => DIETARY_ACRONYMS[claim.slug])
    .filter((label): label is string => Boolean(label))

const getMeta = (recipe: RecipeCarouselCardRecipe): string | null => {
  const parts: string[] = []
  if (typeof recipe.cookTimeMins === 'number') parts.push(`${recipe.cookTimeMins} mins`)
  if (typeof recipe.kcal === 'number') parts.push(`${recipe.kcal} kcal`)
  return parts.length > 0 ? parts.join(' | ') : null
}

export const RecipeCarouselCard = ({ recipe }: Props) => {
  const meta = getMeta(recipe)
  const badges = getDietaryBadges(recipe)

  return (
    <Card type="button" aria-label={recipe.ariaLabel ?? recipe.title} onClick={recipe.onClick}>
      <Box height={IMAGE_HEIGHT} position={DisplayPosition.Relative}>
        <Image
          alt={recipe.imageAlt}
          fit="cover"
          height={IMAGE_HEIGHT}
          src={recipe.imageUrl}
          width="100%"
          style={{ display: 'block' }}
        />
        {badges.length > 0 ? (
          <Box
            aria-label="Dietary information"
            display={Display.Flex}
            gap={1}
            left="8px"
            position={DisplayPosition.Absolute}
            paddingH={1}
            paddingV={1}
            top="8px"
          >
            {badges.map((label) => (
              <Box
                key={`${recipe.id}-${label}`}
                bg={Color.ColdGrey_800}
                paddingH={1}
                paddingV={1}
                style={{ borderRadius: '100%' }}
              >
                <Text
                  color={Color.White}
                  fontFamily={FontFamily.Bold}
                  fontWeight={FontWeight.Bold}
                  size={1}
                  style={{
                    borderRadius: '100%',
                    backgroundColor: Color.ColdGrey_700,
                    width: '24px',
                    height: '24px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {label}
                </Text>
              </Box>
            ))}
          </Box>
        ) : null}
      </Box>
      <Box
        display={Display.Flex}
        flexDirection={FlexDirection.Column}
        gap={4}
        paddingH={3}
        paddingV={3}
        justifyContent={JustifyContent.SpaceBetween}
        height="100%"
      >
        <Body2 color={Color.ColdGrey_900} fontWeight={FontWeight.Bold} size={1}>
          {recipe.title}
        </Body2>
        {meta ? (
          <Text color={Color.ColdGrey_600} size={1}>
            {meta}
          </Text>
        ) : null}
      </Box>
    </Card>
  )
}
